import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView, Platform } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { RootState } from '../../src/redux/store';
import { Colors } from '../../src/styles/theme';
import { useAppDispatch, useAppSelector } from '../../src/redux/hooks';
import { changeAppLanguage, selectCurrentLanguage, selectIsChangingLanguage } from '../../src/redux/languageSlice';

const UserProfileScreen: React.FC = () => {
  const router = useRouter();
  const { user } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const languageDispatch = useAppDispatch();
  const currentLanguage = useAppSelector(selectCurrentLanguage);
  const isChangingLanguage = useAppSelector(selectIsChangingLanguage);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const handleLanguageChange = async (language: 'en' | 'fr') => {
    if (language === currentLanguage) return;

    try {
      await languageDispatch(changeAppLanguage(language)).unwrap();
      setModalMessage(
        t('language.currentLanguage') + ': ' + (language === 'fr' ? t('language.french') : t('language.english'))
      );
      setShowModal(true);
    } catch (error) {
      console.error('[userProfile] language change failed', error);
      setModalMessage(t('errors.languageChangeFailed'));
      setShowModal(true);
    }
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ padding: 20 }}>
      {/* Header with Home Button */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.homeButton} onPress={() => router.push('/(app)')}>
          <Text style={styles.homeButtonText}>{t('navigation.home')}</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>{t('navigation.profile')}</Text>

      {/* User Information */}
      {user ? (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('profile.accountInformation')}</Text>
          <Text style={styles.text}>{t('auth.email')}: {user.email}</Text>
        </View>
      ) : (
        <View style={styles.section}>
          <Text style={styles.text}>{t('auth.guestMode')}</Text>
          <TouchableOpacity
            style={styles.signInButton}
            onPress={() => router.push('/auth')}
          >
            <Text style={styles.signInButtonText}>{t('auth.signIn')}</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Language Settings */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('language.changeLanguage')}</Text>
        <Text style={styles.sectionDescription}>
          {t('language.currentLanguage')}: {currentLanguage === 'fr' ? t('language.french') : t('language.english')}
        </Text>

        <View style={styles.languageButtons}>
          <TouchableOpacity
            style={[
              styles.languageButton,
              currentLanguage === 'en' && styles.languageButtonActive,
              isChangingLanguage && styles.languageButtonDisabled
            ]}
            onPress={() => handleLanguageChange('en')}
            disabled={isChangingLanguage}
          >
            <Text style={[
              styles.languageButtonText,
              currentLanguage === 'en' && styles.languageButtonTextActive
            ]}>
              {t('language.english')}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.languageButton,
              currentLanguage === 'fr' && styles.languageButtonActive,
              isChangingLanguage && styles.languageButtonDisabled
            ]}
            onPress={() => handleLanguageChange('fr')}
            disabled={isChangingLanguage}
          >
            <Text style={[
              styles.languageButtonText,
              currentLanguage === 'fr' && styles.languageButtonTextActive
            ]}>
              {t('language.french')}
            </Text>
          </TouchableOpacity>
        </View>

        {isChangingLanguage && (
          <Text style={styles.changingText}>{t('language.changing')}</Text>
        )}
      </View>

      {/* Modal for messages */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={showModal}
        onRequestClose={() => setShowModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalMessage}>{modalMessage}</Text>
            <TouchableOpacity
              style={styles.modalButton}
              onPress={() => setShowModal(false)}
            >
              <Text style={styles.modalButtonText}>OK</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background,
  },
  header: {
    width: '100%',
    marginBottom: 20,
    alignItems: 'flex-start',
  },
  homeButton: {
    backgroundColor: Colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  homeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 30,
    color: Colors.text,
    textAlign: 'center',
  },
  signInButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginTop: 12,
    alignItems: 'center',
  },
  signInButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  section: {
    width: '100%',
    marginBottom: 30,
    padding: 20,
    backgroundColor: 'white',
    borderRadius: 12,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      },
    }),
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: Colors.text,
    marginBottom: 10,
  },
  sectionDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 20,
  },
  text: {
    fontSize: 16,
    color: Colors.text,
    marginBottom: 10,
  },
  languageButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  languageButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderRadius: 12,
    backgroundColor: '#f8f9fa',
    borderWidth: 2,
    borderColor: '#e9ecef',
    marginHorizontal: 6,
  },
  languageButtonActive: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  },
  languageButtonDisabled: {
    opacity: 0.5,
  },
  languageFlag: {
    fontSize: 20,
    marginRight: 8,
  },
  languageButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text,
  },
  languageButtonTextActive: {
    color: 'white',
  },
  changingText: {
    textAlign: 'center',
    fontSize: 14,
    color: '#666',
    marginTop: 10,
    fontStyle: 'italic',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    margin: 20,
    minWidth: 280,
    maxWidth: 340,
    alignItems: 'center',
  },
  modalMessage: {
    fontSize: 16,
    color: Colors.text,
    marginBottom: 20,
    textAlign: 'center',
  },
  modalButton: {
    backgroundColor: Colors.primary,
    paddingVertical: 10,
    paddingHorizontal: 30,
    borderRadius: 8,
  },
  modalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default UserProfileScreen;
